import { webCore } from '@codes/web-core';

import { ListResult } from '../types';

/**
 * Interface for API configuration
 */
export interface ApiConfig {
    /** API endpoint URL (default: VITE_BACKEND_ENDPOINT) */
    endpoint?: string;
    /** Base URL path (e.g., '/api/v1') */
    baseURL?: string;
    /** Custom paths for each API endpoint */
    paths?: {
        /** List retrieval path (default: '') */
        list?: string;
        /** Detail retrieval path (default: '/${id}/detail') */
        detail?: string;
        /** Creation path (default: '/0') */
        create?: string;
        /** Update path (default: '/${id}') */
        update?: string;
        /** Deletion path (default: '/${id}') */
        delete?: string;
    };
}

/**
 * Interface for API request configuration
 */
export interface RequestConfig {
    /** URL query parameters */
    params?: Record<string, any>;
    /** Request body */
    body?: Record<string, any>;
    /** Request headers */
    headers?: Record<string, string>;
}

// NOTE:
// const api = new BaseApi<View, Body>('resources');
// GET /resources
// GET /resources/123/detail
// POST /resources/0
// PUT /resources/123
// DELETE /resources/123

// const api = new BaseApi<View, Body>('resources', {
//     paths: {
//         list: '/list',
//         detail: '/${id}/info',
//         create: '/new',
//         update: '/${id}/update',
//         delete: '/${id}/remove'
//     }
// });
// GET /resources/list
// GET /resources/123/info
// POST /resources/new
// PUT /resources/123/update
// DELETE /resources/123/remove

/**
 * Base API class for handling REST API operations
 * @template TView - Type for API response data
 * @template TBody - Type for API request body
 */
export class BaseApi<TView, TBody> {
    private readonly endpoint: string;
    private readonly baseURL: string;
    private readonly paths: Required<NonNullable<ApiConfig['paths']>>;

    /**
     * BaseApi 생성자
     * @param {string} resourcePath - 리소스 경로 (예: 'projects', 'workspaces')
     * @param {ApiConfig} [config] - API 설정
     */
    constructor(private readonly resourcePath: string, config?: ApiConfig) {
        this.endpoint = config?.endpoint?.toLowerCase() ?? import.meta.env.VITE_BACKEND_ENDPOINT.toLowerCase();
        this.baseURL = config?.baseURL ?? '';
        this.paths = {
            list: config?.paths?.list ?? '',
            detail: config?.paths?.detail ?? '/${id}/detail',
            create: config?.paths?.create ?? '/0',
            update: config?.paths?.update ?? '/${id}',
            delete: config?.paths?.delete ?? '/${id}',
        };
    }

    /**
     * Generates a full URL by applying parameters to the URL template
     * @private
     * @param pathTemplate - URL path template
     * @param params - URL parameters object
     * @returns Complete URL string
     */
    private getFullUrl(pathTemplate: string, params: Record<string, string> = {}): string {
        let path = pathTemplate;
        Object.entries(params).forEach(([key, value]) => {
            path = path.replace(`${key}`, value);
        });
        return `${this.endpoint}${this.baseURL}/${this.resourcePath}${path}`;
    }

    /**
     * Retrieves a list of resources
     * @param config - API request configuration (params, body, headers)
     * @returns List of resources with pagination information
     */
    async fetchList(config?: RequestConfig): Promise<ListResult<TView>> {
        const request = webCore.buildSignedRequest({
            method: 'GET',
            baseURL: this.getFullUrl(this.paths.list),
        });

        if (config?.params) {
            request.setParams(config.params);
        }

        if (config?.body) {
            request.setBody(config.body);
        }

        if (config?.headers) {
            request.addHeaders(config.headers);
        }

        const { data } = await request.execute<ListResult<TView>>();
        return { ...data };
    }

    /**
     * Retrieves a single resource by ID
     * @param id - Resource identifier
     * @param config - API request configuration (params, body, headers)
     * @returns Resource detail information
     * @throws Error if ID is not provided
     */
    async fetchById(id: string, config?: RequestConfig): Promise<TView> {
        if (!id) {
            throw new Error(`fetch${this.resourcePath}ById: @id is required`);
        }

        const request = webCore.buildSignedRequest({
            method: 'GET',
            baseURL: this.getFullUrl(this.paths.detail, { '${id}': id }),
        });

        if (config?.params) {
            request.setParams(config.params);
        }
        if (config?.body) {
            request.setBody(config.body);
        }
        if (config?.headers) {
            request.addHeaders(config.headers);
        }

        const { data } = await request.execute<TView>();
        return { ...data };
    }

    /**
     * Creates a new resource
     * @param config - API request configuration (params, body, headers)
     * @returns Created resource information
     */
    async create(config?: RequestConfig): Promise<TView> {
        const request = webCore.buildSignedRequest({
            method: 'POST',
            baseURL: this.getFullUrl(this.paths.create),
        });

        if (config?.params) {
            request.setParams(config.params);
        }
        if (config?.body) {
            request.setBody(config.body);
        }
        if (config?.headers) {
            request.addHeaders(config.headers);
        }

        const { data } = await request.execute<TView>();
        return data;
    }

    /**
     * Updates an existing resource
     * @param id - Resource identifier to update
     * @param config - API request configuration (params, body, headers)
     * @returns Updated resource information
     * @throws Error if ID is not provided
     */
    async update(id: string, config?: RequestConfig): Promise<TView> {
        if (!id) {
            throw new Error(`update${this.resourcePath}: @id is required`);
        }

        const request = webCore.buildSignedRequest({
            method: 'PUT',
            baseURL: this.getFullUrl(this.paths.update, { '${id}': id }),
        });

        if (config?.params) {
            request.setParams(config.params);
        }
        if (config?.body) {
            request.setBody(config.body);
        }
        if (config?.headers) {
            request.addHeaders(config.headers);
        }

        const response = await request.execute<TView>();
        return response.data;
    }

    /**
     * Deletes a resource
     * @param id - Resource identifier to delete
     * @param config - API request configuration (params, body, headers)
     * @returns Deleted resource information
     * @throws Error if ID is not provided
     */
    async delete(id: string, config?: RequestConfig): Promise<TView> {
        if (!id) {
            throw new Error(`delete${this.resourcePath}: @id is required`);
        }

        const request = webCore
            .buildSignedRequest({
                method: 'DELETE',
                baseURL: this.getFullUrl(this.paths.delete, { '${id}': id }),
            })
            .setBody({});

        if (config?.params) {
            request.setParams(config.params);
        }
        if (config?.body) {
            request.setBody(config.body);
        }
        if (config?.headers) {
            request.addHeaders(config.headers);
        }

        const { data } = await request.execute<TView>();
        return data;
    }
}
